import React, { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  TestTube,
  Play,
  Pause,
  Server,
  ExternalLink,
  CheckCircle,
  XCircle,
  Download,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import Button from '../components/ui/Button'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import Input from '../components/ui/Input'
import { StatusBadge } from '../components/ui/Badge'
import Modal from '../components/ui/Modal'
import { Select } from '../components/ui/Input'
import { formatNumber, timeAgo, cn } from '../utils/cn'

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// API service (sẽ được thay bằng thực tế)
const readApiError = async (response: Response, fallback: string) => {
  const body = await response.json().catch(() => null)
  if (typeof body?.detail === 'string') return body.detail
  if (body?.detail?.validation_errors) return body.detail.validation_errors.join(', ')
  return fallback
}

const api = {
  getProviders: async () => {
    const response = await fetch('/api/v1/providers/', {
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to fetch providers'))
    return response.json()
  },

  createProvider: async (data: any) => {
    const response = await fetch('/api/v1/providers/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to create provider'))
    return response.json()
  },

  updateProvider: async (id: number, data: any) => {
    const response = await fetch(`/api/v1/providers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to update provider'))
    return response.json()
  },

  deleteProvider: async ({ id, deleteRemote = false }: { id: number; deleteRemote?: boolean }) => {
    const response = await fetch(`/api/v1/providers/${id}?delete_remote=${deleteRemote}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to delete provider'))
  },

  testProvider: async (id: number) => {
    const response = await fetch(`/api/v1/providers/${id}/test`, {
      method: 'POST',
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to test provider'))
    return response.json()
  },

  syncProvider: async (id: number, dryRun = false) => {
    const response = await fetch(`/api/v1/providers/${id}/sync?dry_run=${dryRun}`, {
      method: 'POST',
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to sync provider'))
    return response.json()
  },
  getV2BoardPanels: async () => {
    const response = await fetch('/api/v1/v2board-panels/', { headers: authHeaders() })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to fetch V2Board panels'))
    return response.json()
  },
}

// Provider type options
const providerTypes = [
  { value: 'clash', label: 'Clash' },
  { value: 'v2ray', label: 'V2Ray' },
  { value: 'shadowsocks', label: 'Shadowsocks' },
  { value: 'trojan', label: 'Trojan' },
  { value: 'raw', label: 'Raw' },
  { value: 'custom_api', label: 'Custom API' },
]

const buildProviderFormData = (provider?: any) => ({
  name: provider?.name ?? '',
  description: provider?.description ?? '',
  type: provider?.type ?? 'clash',
  subscription_url: provider?.subscription_url ?? '',
  enabled: provider?.enabled ?? true,
  priority: provider?.priority ?? 1,
  nodes_per_sync: provider?.nodes_per_sync ?? 0,
  sync_interval: provider?.sync_interval ?? 3600,
  v2board_panel_id: provider?.v2board_panel_id ?? '',
  v2board_group_id: provider?.v2board_group_id ?? provider?.v2board_integration?.group_id ?? '',
  validate_nodes: provider?.validate_nodes ?? true,
  remove_missing: provider?.remove_missing ?? true,
  deduplicate: provider?.deduplicate ?? true,
})

// Provider form component
interface ProviderFormProps {
  provider?: any
  panels?: any[]
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
}

const ProviderForm: React.FC<ProviderFormProps> = ({
  provider,
  panels = [],
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState(() => buildProviderFormData(provider))

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setFormData(buildProviderFormData(provider))
      setErrors({})
      setIsSubmitting(false)
    }
  }, [isOpen, provider])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors({})

    try {
      const payload = {
        ...formData,
        v2board_group_id: formData.v2board_group_id || null,
        v2board_panel_id: formData.v2board_panel_id || null,
      }
      await onSubmit(payload)
      onClose()
      toast.success(provider ? 'Provider updated successfully' : 'Provider created successfully')
    } catch (error: any) {
      const errorData = await error.json?.()
      if (errorData?.detail?.validation_errors) {
        const validationErrors: Record<string, string> = {}
        errorData.detail.validation_errors.forEach((err: string) => {
          const match = err.match(/([^:]+): (.+)/)
          if (match) {
            const [_, field, message] = match
            validationErrors[field] = message
          }
        })
        setErrors(validationErrors)
      } else {
        toast.error(error.message || 'Failed to save provider')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={provider ? 'Edit Provider' : 'Add New Provider'}
      description="Configure VPN provider subscription details"
      size="lg"
      loading={isSubmitting}
      hideFooter
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Provider Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={errors.name}
              required
              leftIcon={<Server className="w-4 h-4" />}
              placeholder="e.g., Premium VPN"
            />

            <Select
              label="Provider Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              options={providerTypes}
              required
            />
          </div>

          <Input
            label="Subscription URL"
            value={formData.subscription_url}
            onChange={(e) => setFormData({ ...formData, subscription_url: e.target.value })}
            error={errors.subscription_url}
            required
            placeholder="https://provider.com/api/subscription"
          />

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description for this provider"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="V2Board Panel"
              value={formData.v2board_panel_id || ''}
              onChange={(e) => setFormData({ ...formData, v2board_panel_id: e.target.value ? parseInt(e.target.value) : '' })}
              options={[
                { value: '', label: 'Use default/global panel' },
                ...panels.map((panel: any) => ({ value: panel.id, label: panel.name })),
              ]}
              helperText="Choose which V2Board this provider syncs into"
            />

            <Input
              label="V2Board Group ID"
              type="number"
              value={formData.v2board_group_id || ''}
              onChange={(e) => setFormData({ ...formData, v2board_group_id: e.target.value ? parseInt(e.target.value) : '' })}
              placeholder="e.g., 11"
              helperText="Group ID inside selected V2Board"
            />
          </div>

          {/* Sync Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              min={1}
              max={10}
              helperText="Higher number = higher priority"
            />

            <Input
              label="Nodes per Sync"
              type="number"
              value={formData.nodes_per_sync}
              onChange={(e) => setFormData({ ...formData, nodes_per_sync: parseInt(e.target.value || '0') })}
              min={0}
              max={10000}
              helperText="Use 0 to import all nodes"
            />

            <Input
              label="Sync Interval (seconds)"
              type="number"
              value={formData.sync_interval}
              onChange={(e) => setFormData({ ...formData, sync_interval: parseInt(e.target.value) })}
              min={300}
              helperText="Minimum 300 seconds (5 minutes)"
            />
          </div>

          {/* Toggles */}
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 cursor-pointer">
              <div>
                <span className="font-medium text-secondary-900 dark:text-white">
                  Enabled
                </span>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  Enable or disable this provider
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 cursor-pointer">
              <div>
                <span className="font-medium text-secondary-900 dark:text-white">
                  Validate Nodes
                </span>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  Test node connectivity before syncing
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.validate_nodes}
                onChange={(e) => setFormData({ ...formData, validate_nodes: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 cursor-pointer">
              <div>
                <span className="font-medium text-secondary-900 dark:text-white">
                  Remove Missing Nodes
                </span>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  Remove nodes no longer in subscription
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.remove_missing}
                onChange={(e) => setFormData({ ...formData, remove_missing: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 cursor-pointer">
              <div>
                <span className="font-medium text-secondary-900 dark:text-white">
                  Deduplicate Nodes
                </span>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  Remove duplicate nodes from same provider
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.deduplicate}
                onChange={(e) => setFormData({ ...formData, deduplicate: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {provider ? 'Update Provider' : 'Create Provider'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

const Providers = () => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editingProvider, setEditingProvider] = useState<any>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null)
  const [deleteRemoteNodes, setDeleteRemoteNodes] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [testModalOpen, setTestModalOpen] = useState(false)

  // Fetch providers
  const { data: providers = [], isLoading, error } = useQuery({
    queryKey: ['providers'],
    queryFn: api.getProviders,
  })
  const { data: panels = [] } = useQuery({
    queryKey: ['v2board-panels'],
    queryFn: api.getV2BoardPanels,
  })

  // Create provider mutation
  const createMutation = useMutation({
    mutationFn: api.createProvider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] })
    },
  })

  // Update provider mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateProvider(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] })
    },
  })

  // Delete provider mutation
  const deleteMutation = useMutation({
    mutationFn: api.deleteProvider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] })
    },
  })

  // Test provider mutation
  const testMutation = useMutation({
    mutationFn: api.testProvider,
    onSuccess: (data) => {
      setTestResult(data)
      setTestModalOpen(true)
    },
  })

  // Sync provider mutation
  const syncMutation = useMutation({
    mutationFn: ({ id, dryRun }: { id: number; dryRun: boolean }) => api.syncProvider(id, dryRun),
    onSuccess: (data) => {
      if (data.v2board_skipped_reason) {
        toast.success(`Imported locally: ${data.local_nodes_imported || 0} nodes. V2Board was not changed.`, { duration: 8000 })
      } else if (data.nodes_failed > 0) {
        toast.error(`Sync partial: ${data.nodes_synced} synced, ${data.nodes_failed} failed`, { duration: 6000 })
      } else {
        toast.success(data.dry_run ? 'Test sync completed' : `Sync completed: ${data.nodes_synced} nodes`)
      }
      queryClient.invalidateQueries({ queryKey: ['providers'] })
      queryClient.invalidateQueries({ queryKey: ['nodes'] })
    },
  })

  // Filter providers
  const filteredProviders = providers.filter((provider: any) => {
    const matchesSearch =
      provider.name.toLowerCase().includes(search.toLowerCase()) ||
      provider.description?.toLowerCase().includes(search.toLowerCase()) ||
      provider.subscription_url.toLowerCase().includes(search.toLowerCase())

    const matchesStatus = statusFilter === 'all' || provider.status === statusFilter
    const matchesType = typeFilter === 'all' || provider.type === typeFilter

    return matchesSearch && matchesStatus && matchesType
  })

  const handleSubmit = async (data: any) => {
    if (editingProvider) {
      await updateMutation.mutateAsync({ id: editingProvider.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const handleDelete = async (provider: any) => {
    try {
      await deleteMutation.mutateAsync({ id: provider.id, deleteRemote: deleteRemoteNodes })
      toast.success('Provider deleted successfully')
      setDeleteConfirm(null)
      setDeleteRemoteNodes(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete provider')
    }
  }

  const handleTest = async (provider: any) => {
    try {
      await testMutation.mutateAsync(provider.id)
    } catch (error: any) {
      toast.error(error.message || 'Failed to test provider')
    }
  }

  const handleSync = async (provider: any, dryRun = false) => {
    try {
      await syncMutation.mutateAsync({ id: provider.id, dryRun })
    } catch (error: any) {
      toast.error(error.message || 'Failed to sync provider')
    }
  }

  const handleConfirmImport = async () => {
    if (!testResult?.provider) return
    await handleSync(testResult.provider, false)
    setTestModalOpen(false)
  }

  const handleEnableToggle = async (provider: any, enable: boolean) => {
    try {
      await updateMutation.mutateAsync({
        id: provider.id,
        data: { enabled: enable },
      })
      toast.success(`Provider ${enable ? 'enabled' : 'disabled'} successfully`)
    } catch (error: any) {
      toast.error(error.message || 'Failed to update provider')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 rounded-lg bg-secondary-200 dark:bg-secondary-700" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
          <div className="h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-error-100 dark:bg-error-900/30 flex items-center justify-center mx-auto mb-4">
            <Server className="w-8 h-8 text-error0" />
          </div>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
            Failed to load providers
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            {error.message}
          </p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Providers Management
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            Manage VPN providers and their subscription links
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditingProvider(null)
            setShowForm(true)
          }}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add Provider
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search providers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'error', label: 'Error' },
                { value: 'testing', label: 'Testing' },
                { value: 'pending', label: 'Pending' },
              ]}
              className="w-40"
            />
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                ...providerTypes,
              ]}
              className="w-40"
            />
            <Button variant="ghost" size="sm" leftIcon={<Filter className="w-4 h-4" />}>
              More Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {providers.length}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Total Providers
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">
              {providers.filter((p: any) => p.enabled).length}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Active Providers
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
              {providers.reduce((sum: number, p: any) => sum + p.total_nodes, 0)}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Total Nodes
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-info-600 dark:text-info-400">
              {Math.round(
                providers.reduce((sum: number, p: any) => sum + p.success_rate, 0) /
                Math.max(providers.length, 1)
              )}%
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Avg Success Rate
            </div>
          </div>
        </Card>
      </div>

      {/* Providers Table */}
      <Card>
        <CardHeader title="Providers List" description={`Showing ${filteredProviders.length} of ${providers.length} providers`} />
        <CardContent>
          {filteredProviders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center mx-auto mb-4">
                <Server className="w-8 h-8 text-secondary-500 dark:text-secondary-400" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                No providers found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                Try adjusting your search or add a new provider
              </p>
              <Button
                variant="primary"
                onClick={() => setShowForm(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add First Provider
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Provider
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Nodes
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Performance
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Last Sync
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredProviders.map((provider: any) => (
                    <tr key={provider.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            provider.enabled
                              ? "bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                              : "bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400"
                          )}>
                            <Server className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-medium text-secondary-900 dark:text-white">
                              {provider.name}
                            </div>
                            <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate max-w-xs">
                              {provider.subscription_url}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-400">
                          {provider.type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <StatusBadge
                            status={provider.health_status}
                            dot
                            size="sm"
                          />
                          <button
                            onClick={() => handleEnableToggle(provider, !provider.enabled)}
                            className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700"
                          >
                            {provider.enabled ? (
                              <Pause className="w-4 h-4 text-warning-500" />
                            ) : (
                              <Play className="w-4 h-4 text-success-500" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-success-600 dark:text-success-400">
                              {provider.node_stats.active} active
                            </span>
                            <span className="text-xs text-secondary-500">
                              of {provider.node_stats.total}
                            </span>
                          </div>
                          <div className="h-1 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-success-500 rounded-full transition-all duration-300"
                              style={{
                                width: `${(provider.node_stats.active / provider.node_stats.total) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-secondary-900 dark:text-white">
                            {provider.performance.success_rate}% success
                          </div>
                          <div className="text-xs text-secondary-500 dark:text-secondary-400">
                            {provider.performance.avg_response_time}ms avg
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <div className="text-sm text-secondary-900 dark:text-white">
                            {provider.last_synced_at ? timeAgo(provider.last_synced_at) : 'Never'}
                          </div>
                          <div className="text-xs text-secondary-500 dark:text-secondary-400">
                            {provider.last_sync_status || 'Not synced'}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(provider)}
                            loading={testMutation.isPending && testMutation.variables === provider.id}
                            leftIcon={<TestTube className="w-3 h-3" />}
                          >
                            Test
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSync(provider, true)}
                            loading={syncMutation.isPending && syncMutation.variables.id === provider.id && syncMutation.variables.dryRun}
                            leftIcon={<Eye className="w-3 h-3" />}
                          >
                            Dry Run
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingProvider(provider)
                              setShowForm(true)
                            }}
                            leftIcon={<Edit className="w-3 h-3" />}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeleteRemoteNodes(false)
                              setDeleteConfirm(provider)
                            }}
                            leftIcon={<Trash2 className="w-3 h-3" />}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Modal */}
      <ProviderForm
        provider={editingProvider}
        panels={panels}
        isOpen={showForm}
        onClose={() => {
          setShowForm(false)
          setEditingProvider(null)
        }}
        onSubmit={handleSubmit}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Provider"
        size="sm"
        hideFooter
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Delete "{deleteConfirm?.name}" from Casub?
          </p>
          <label className="flex items-start gap-3 rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-3">
            <input
              type="checkbox"
              checked={deleteRemoteNodes}
              onChange={(e) => setDeleteRemoteNodes(e.target.checked)}
              className="mt-1 w-4 h-4 text-warning-600 rounded focus:ring-warning-500"
            />
            <span className="text-sm text-warning-800 dark:text-warning-200">
              Also delete tracked remote nodes from V2Board. Only nodes previously created and tracked by Casub will be deleted.
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirm)} loading={deleteMutation.isPending}>
              Delete Provider
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Result Modal */}
      <Modal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title="Subscription Test Result"
        size="lg"
        hideFooter
      >
        {testResult && (
          <div className="space-y-4">
            <div className={cn(
              "p-4 rounded-lg",
              testResult.success
                ? "bg-success-50 dark:bg-success-900/30 border border-success-200 dark:border-success-700"
                : "bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-700"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center",
                  testResult.success
                    ? "bg-success-100 dark:bg-success-800 text-success-600 dark:text-success-400"
                    : "bg-error-100 dark:bg-error-800 text-error-600 dark:text-error-400"
                )}>
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900 dark:text-white">
                    Test {testResult.success ? 'Successful' : 'Failed'}
                  </h4>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">
                    Client: {testResult.client_profile || 'Auto'} {testResult.url_variant ? `(${testResult.url_variant})` : ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="text-sm text-secondary-500 dark:text-secondary-400">
                  Content Length
                </div>
                <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {Number(testResult.content_length || 0).toLocaleString()} bytes
                </div>
              </div>
              <div className="p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="text-sm text-secondary-500 dark:text-secondary-400">
                  Nodes Found
                </div>
                <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {testResult.nodes_found}
                </div>
                {typeof testResult.raw_nodes_found === 'number' && testResult.raw_nodes_found !== testResult.nodes_found && (
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">
                    {testResult.raw_nodes_found} raw, filtered placeholders
                  </div>
                )}
              </div>
              <div className="p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="text-sm text-secondary-500 dark:text-secondary-400">
                  V2Board Ready
                </div>
                <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {testResult.supported_nodes ?? 0}
                </div>
              </div>
              <div className="p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="text-sm text-secondary-500 dark:text-secondary-400">
                  Unsupported
                </div>
                <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {testResult.unsupported_nodes ?? 0}
                </div>
              </div>
            </div>

            {Array.isArray(testResult.unsupported_protocols) && testResult.unsupported_protocols.length > 0 && (
              <div className="p-3 rounded-lg border border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20">
                <p className="text-sm text-warning-700 dark:text-warning-300">
                  Unsupported by V2Board sync: {testResult.unsupported_protocols.join(', ')}
                </p>
              </div>
            )}

            {Array.isArray(testResult.fetch_attempts) && testResult.fetch_attempts.length > 0 && (
              <div className="p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/30">
                <div className="text-sm font-medium text-secondary-900 dark:text-white mb-2">
                  Fetch Attempts
                </div>
                <div className="space-y-1 max-h-28 overflow-auto">
                  {testResult.fetch_attempts.slice(0, 8).map((attempt: any, index: number) => (
                    <div key={index} className="text-xs text-secondary-600 dark:text-secondary-400">
                      {attempt.client_profile} / {attempt.url_variant}: {attempt.usable_nodes ?? 0} usable
                      {attempt.raw_nodes ? `, ${attempt.raw_nodes} raw` : ''}
                      {attempt.unsupported_content ? ', unsupported response' : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(testResult.nodes_preview) && testResult.nodes_preview.length > 0 && (
              <div>
                <h5 className="font-medium text-secondary-900 dark:text-white mb-2">
                  Node Preview
                </h5>
                <div className="max-h-64 overflow-auto rounded-lg border border-secondary-200 dark:border-secondary-700">
                  {testResult.nodes_preview.map((node: any, index: number) => (
                    <div key={`${node.protocol}-${node.address}-${node.port}-${index}`} className="px-3 py-2 border-b border-secondary-100 dark:border-secondary-800 last:border-b-0">
                      <div className="font-medium text-sm text-secondary-900 dark:text-white">{node.name}</div>
                      <div className="text-xs text-secondary-500 dark:text-secondary-400">
                        {node.protocol}://{node.address}:{node.port || '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h5 className="font-medium text-secondary-900 dark:text-white mb-2">
                Content Preview
              </h5>
              <div className="p-4 rounded-lg bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700">
                <pre className="text-xs whitespace-pre-wrap break-words font-mono">
                  {testResult.content_preview}
                  {testResult.content_length > 500 && '...'}
                </pre>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                variant="ghost"
                onClick={() => setTestModalOpen(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmImport}
                loading={syncMutation.isPending}
                disabled={!testResult.success}
                leftIcon={<Download className="w-4 h-4" />}
              >
                Confirm Import
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Providers

